import React, { useState, useCallback, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import UserActions from './component'
import {
  CANCEL_PENDING_INVITE_TEXT,
  CANT_BE_REMOVED_TEXT,
  CHANGE_TO_TEXT,
  REMOVE_USER_TEXT,
  ROSTER_INVITE_CANT_BE_REMOVED_TEXT,
  ROSTER_USER_CANT_BE_REMOVED_TEXT,
  SEND_REMINDER_TEXT,
} from './constants'
import { AccessLevel, InvitationStatus } from 'lib/util'

/**
 * Custom hook to handle clicking outside a referenced element
 * Only attaches listeners when the menu is open for better performance
 */
const useClickOutside = (ref, isOpen, onClickOutside) => {
  useEffect(() => {
    if (!isOpen) return

    const handleClick = event => {
      if (ref.current && !ref.current.contains(event.target)) {
        onClickOutside()
      }
    }

    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [ref, isOpen, onClickOutside])
}

export const UserActionsContainer = ({
  applicableRoles,
  currentUserId,
  handleCancelUserAccess,
  handleGrantNewRole,
  handleRemindEmail,
  id: rowId,
  user,
}) => {
  const [areActionsOpen, setAreActionsOpen] = useState(false)
  const containerRef = useRef(null)

  // Derived state
  const currentRole = user.roles[0]
  const isCurrentUser = currentUserId === user.userId
  const isActive = user.status === InvitationStatus.Active
  const isInvited = user.status === InvitationStatus.Invited

  // Close menu handler (stable reference for useClickOutside)
  const closeMenu = useCallback(() => setAreActionsOpen(false), [])

  // Click outside handling
  useClickOutside(containerRef, areActionsOpen, closeMenu)

  // Event handlers
  const toggleActions = useCallback(() => {
    setAreActionsOpen(prev => !prev)
  }, [])

  const handleRemoveUser = useCallback(() => {
    handleCancelUserAccess(user)
    closeMenu()
  }, [handleCancelUserAccess, user, closeMenu])

  const handleRemindUser = useCallback(() => {
    handleRemindEmail(user)
    closeMenu()
  }, [handleRemindEmail, user, closeMenu])

  const handleRoleChange = useCallback(
    newRole => {
      handleGrantNewRole({ ...user, sendEmail: true }, newRole)
      closeMenu()
    },
    [handleGrantNewRole, user, closeMenu],
  )

  // Build actions list
  const buildActions = () => {
    const actions = []

    // Add reminder action for invited users (appears first)
    if (isInvited) {
      actions.push({
        text: SEND_REMINDER_TEXT,
        handler: handleRemindUser,
        type: 'remind',
      })
    }

    // Add role change actions for active/invited users
    if (isActive || isInvited) {
      applicableRoles
        .filter(
          role => role?.role?.toLowerCase() !== currentRole?.toLowerCase(),
        )
        .forEach(role => {
          actions.push({
            text: `${CHANGE_TO_TEXT} ${AccessLevel[role?.role]?.friendlyName}`,
            handler: () => handleRoleChange(role?.role),
            type: `grant-${role?.role?.toLowerCase()}`,
          })
        })
    }

    // Add remove/cancel action (appears last)
    actions.push({
      text: isActive ? REMOVE_USER_TEXT : CANCEL_PENDING_INVITE_TEXT,
      tooltipText: isActive
        ? ROSTER_USER_CANT_BE_REMOVED_TEXT
        : ROSTER_INVITE_CANT_BE_REMOVED_TEXT,
      handler: handleRemoveUser,
      type: 'remove',
    })

    return actions
  }

  // Computed text for current user warning
  const cantRemoveEditText = CANT_BE_REMOVED_TEXT.replace(
    /ROLE/g,
    AccessLevel[currentRole]?.friendlyName,
  )

  return (
    <div ref={containerRef}>
      <UserActions
        actions={buildActions()}
        areActionsOpen={areActionsOpen}
        cantRemoveEditText={cantRemoveEditText}
        isCurrentUser={isCurrentUser}
        isRostered={user.isRostered}
        rowId={rowId}
        toggleActions={toggleActions}
      />
    </div>
  )
}

UserActionsContainer.propTypes = {
  applicableRoles: PropTypes.array.isRequired,
  currentUserId: PropTypes.number.isRequired,
  handleCancelUserAccess: PropTypes.func.isRequired,
  handleGrantNewRole: PropTypes.func.isRequired,
  handleRemindEmail: PropTypes.func.isRequired,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  user: PropTypes.object.isRequired,
}

export default UserActionsContainer
